import React, { useState, useRef, useEffect } from 'react';
import { PDFDocument, rgb } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import axiosInstance from '../../api/axiosConfig';
import { toProtectedFileUrl } from '../../utils/protectedFiles';
import './PDFAnnotationModal.css';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PDFAnnotationModal = ({ invoice, mode, onClose }) => {
    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [annotations, setAnnotations] = useState([]);
    const [pdfData, setPdfData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pdfDoc, setPdfDoc] = useState(null);
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const renderTaskRef = useRef(null);

    const tickColor = mode === 'pick' ? 'green' : 'orange';

    useEffect(() => {
        loadPDF();
    }, [invoice]);

    useEffect(() => {
        if (pdfDoc && canvasRef.current) {
            renderPage(pageNumber);
        }
        
        return () => {
            // Cancel any ongoing render task on cleanup
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }
        };
    }, [pdfDoc, pageNumber]);

    const loadPDF = async () => {
        try {
            const response = await axiosInstance.get(toProtectedFileUrl(invoice.pdfUrl), {
                responseType: 'arraybuffer'
            });
            const arrayBuffer = response.data;
            
            // Store a copy immediately to prevent detachment issues
            const arrayBufferCopy = arrayBuffer.slice(0);
            setPdfData(arrayBufferCopy);

            // Load PDF with pdfjs
            const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;
            setPdfDoc(pdf);
            setNumPages(pdf.numPages);
        } catch (error) {
            console.error('Error loading PDF:', error);
            alert('Failed to load PDF');
        }
    };

    const renderPage = async (pageNum) => {
        if (!pdfDoc || !canvasRef.current) return;

        // Cancel any ongoing render task
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
        }

        try {
            const page = await pdfDoc.getPage(pageNum);
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            const viewport = page.getViewport({ scale: 1.5 });
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };

            renderTaskRef.current = page.render(renderContext);
            await renderTaskRef.current.promise;
            renderTaskRef.current = null;
        } catch (error) {
            // Ignore cancellation errors
            if (error.name !== 'RenderingCancelledException') {
                console.error('Error rendering page:', error);
            }
        }
    };

    const handleCanvasClick = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        
        // Simple direct coordinate calculation
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Add annotation at clicked position
        setAnnotations([...annotations, { x, y, page: pageNumber, color: tickColor }]);
    };

    const handleSave = async () => {
        if (annotations.length === 0) {
            alert('Please add at least one mark before saving');
            return;
        }

        setLoading(true);
        try {
            // Use the stored copy directly (already cloned in loadPDF)
            const pdfLibDoc = await PDFDocument.load(pdfData);
            
            // Group annotations by page
            const annotationsByPage = {};
            annotations.forEach(annotation => {
                if (!annotationsByPage[annotation.page]) {
                    annotationsByPage[annotation.page] = [];
                }
                annotationsByPage[annotation.page].push(annotation);
            });

            // Add annotations to each page
            Object.keys(annotationsByPage).forEach(pageNum => {
                const pageIndex = parseInt(pageNum) - 1;
                if (pageIndex < pdfLibDoc.getPageCount()) {
                    const page = pdfLibDoc.getPage(pageIndex);
                    const { height, width } = page.getSize();
                    
                    annotationsByPage[pageNum].forEach(annotation => {
                        // Get canvas dimensions
                        const canvas = canvasRef.current;
                        const rect = canvas.getBoundingClientRect();
                        
                        // Account for canvas scaling
                        const scaleX = canvas.width / rect.width;
                        const scaleY = canvas.height / rect.height;
                        
                        // Convert display coordinates to canvas coordinates
                        const canvasX = annotation.x * scaleX;
                        const canvasY = annotation.y * scaleY;
                        
                        // Convert canvas coordinates to PDF coordinates
                        const pdfX = (canvasX / canvas.width) * width;
                        const pdfY = height - ((canvasY / canvas.height) * height);

                        // Draw X mark - centered by adjusting position
                        const xSize = 18;
                        const xColor = annotation.color === 'green' ? rgb(0, 0.5, 0) : rgb(1, 0.5, 0);
                        
                        page.drawText('X', {
                            x: pdfX - 9,  // Half of size to center
                            y: pdfY - 9,  // Half of size to center
                            size: xSize,
                            color: xColor
                        });
                    });
                }
            });

            // Save the modified PDF
            const pdfBytes = await pdfLibDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            
            // Upload the modified PDF
            const formData = new FormData();
            formData.append('invoice', blob, `${invoice.invoiceNumber}.pdf`);

            await axiosInstance.put(`/api/invoices/${invoice.invoiceNumber}/pdf`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Update status
            await axiosInstance.patch(`/api/invoices/${invoice.invoiceNumber}/status`, {
                [mode]: true
            });

            alert(`Invoice ${mode === 'pick' ? 'picked' : 'verified'} successfully!`);
            onClose();
        } catch (error) {
            console.error('Error saving PDF:', error);
            alert('Failed to save PDF annotations');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pdf-modal-overlay" onClick={onClose}>
            <div className="pdf-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="pdf-modal-header">
                    <h2>{mode === 'pick' ? 'Pick' : 'Verify'} Invoice - {invoice.invoiceNumber}</h2>
                    <button onClick={onClose} className="btn-close">×</button>
                </div>

                <div className="pdf-modal-body">
                    <div className="pdf-viewer-container" ref={containerRef}>
                        <div className="pdf-canvas-wrapper" onClick={handleCanvasClick}>
                            <canvas ref={canvasRef} />
                            
                            {/* Render annotations overlay */}
                            <svg className="annotation-layer" style={{ 
                                width: canvasRef.current?.width || 0, 
                                height: canvasRef.current?.height || 0 
                            }}>
                                {annotations
                                    .filter(a => a.page === pageNumber)
                                    .map((annotation, index) => (
                                        <g key={index}>
                                            {/* Draw X as two crossing lines centered on click point */}
                                            <line
                                                x1={annotation.x - 8}
                                                y1={annotation.y - 8}
                                                x2={annotation.x + 8}
                                                y2={annotation.y + 8}
                                                stroke={annotation.color}
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                            />
                                            <line
                                                x1={annotation.x + 8}
                                                y1={annotation.y - 8}
                                                x2={annotation.x - 8}
                                                y2={annotation.y + 8}
                                                stroke={annotation.color}
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                            />
                                        </g>
                                    ))}
                            </svg>
                        </div>
                    </div>

                    <div className="pdf-controls">
                        <div className="page-controls">
                            <button
                                onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                                disabled={pageNumber <= 1}
                                className="btn-page"
                            >
                                Previous
                            </button>
                            <span>Page {pageNumber} of {numPages || '?'}</span>
                            <button
                                onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                                disabled={pageNumber >= numPages}
                                className="btn-page"
                            >
                                Next
                            </button>
                        </div>

                        <div className="annotation-info">
                            <p>Click anywhere on the PDF to add a <span style={{ color: tickColor }}>X</span> mark</p>
                            <p>Annotations added: {annotations.length}</p>
                        </div>

                        <div className="modal-actions">
                            <button onClick={onClose} className="btn btn-cancel">
                                Cancel
                            </button>
                            <button 
                                onClick={handleSave} 
                                className="btn btn-save"
                                disabled={loading || annotations.length === 0}
                            >
                                {loading ? 'Saving...' : 'Save & Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PDFAnnotationModal;
